import { BrowserRouter, Routes, Route } from 'react-router-dom';
import React, { useEffect } from 'react';
import SignupPage from '@/pages/auth/Signup';
import LoginPage from '@/pages/auth/Login';
import Home from '@/pages/Home';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '@/App.css';
import AddressAutocomplete from '@/pages/AddressAutocomplete';
import { UserProvider } from '@/contexts/UserContext';
import { routes } from '@/constants/routes';
import { RestaurantsProvider } from '@/contexts/RestaurantContext';
import { Suspense } from 'react';
import BODashboard from '@/pages/back-office/BODashboard';
import BORestaurantHome from '@/pages/back-office/BORestaurantHome';
import BORestaurant from '@/pages/back-office/BORestaurant';
import BOOrders from '@/pages/back-office/orders/BOOrders';
import ProductsList from '@/pages/back-office/products/ProductsList';
import ProductCreation from '@/pages/back-office/products/ProductCreation';
import ProductEdition from '@/pages/back-office/products/ProductEdition';
import CategoryCreation from '@/pages/back-office/settings/categories/CategoryCreation';
import CategoriesList from '@/pages/back-office/settings/categories/CategoriesList';
import CategoryEdition from '@/pages/back-office/settings/categories/CategoryEdition';
import SettingMenu from '@/pages/back-office/settings/SettingMenu';
import AppSettings from '@/pages/back-office/settings/AppSettings';
import ReferralSettings from '@/pages/back-office/settings/ReferralSettings';
import { SettingsProvider } from '@/contexts/SettingsContext';
import VatsList from '@/pages/back-office/vats/VatsList';
import VatCreation from '@/pages/back-office/vats/VatCreation';
import VatEdition from '@/pages/back-office/vats/VatEdition';
import UserCreation from '@/pages/back-office/users/UserCreation';
import UserEdition from '@/pages/back-office/users/UserEdition';
import UsersList from '@/pages/back-office/users/UsersList';
import BOMenu from '@/pages/back-office/menu/BOMenu';
import MenuCreation from '@/pages/back-office/menu/MenuCreation';
import MenuEdition from '@/pages/back-office/menu/MenuEdition';
import PrivateRoute from '@/components/PrivateRoute';
import BOAdminPrivateRoute from '@/components/back-office/BOAdminRoute';
import EditUserInfo from '@/pages/front-office/me/EditUserInfo';
import ResetPasswordPage from '@/pages/auth/ResetPassword';
import { OrdersProvider } from '@/contexts/OrderContext';
import Menu from '@/pages/front-office/order/steps/Menu';
import MenuPage from '@/pages/front-office/menu-page/MenuPage';
import CategorieProductSelection from '@/pages/front-office/order/steps/CategorieProductSelection';
import Payment from '@/pages/front-office/order/Payment';
import ProtectedOrdersRoutes from '@/components/front-office/orders/ProtectedOrdersRoutes';
import BOOrderDetails from '@/pages/back-office/orders/BOOrderDetails';
import BOCustomers from '@/pages/back-office/users/BOCustomers';
import PromoDashboard from '@/pages/back-office/promo/PromoDashboard';
import PromoForm from '@/pages/back-office/promo/PromoForm';
import NotificationList from '@/pages/back-office/notifications/NotificationList';
import NotificationForm from '@/pages/back-office/notifications/NotificationForm';
import PopupNotification from '@/components/PopupNotification';
import DeliveryZonesList from '@/pages/back-office/settings/delivery_zones/DeliveryZonesList';
import DeliveryZoneCreationForm from '@/pages/back-office/settings/delivery_zones/DeliveryZoneCreationForm';
import DeliveryZoneEditionForm from '@/pages/back-office/settings/delivery_zones/DeliveryZoneEditionForm';
import PaymentForm from '@/pages/front-office/order/PaymentForm';
import PaymentSuccess from '@/pages/front-office/order/PaymentSuccess';
import BOStatisticsPage from '@/pages/back-office/BOStatisticsPage';
import NotFound from '@/pages/NotFound';
import NoLimitCategoryProductSelection from '@/pages/front-office/order/steps/NoLimitCategoryProductSelection';
import NoLimitListProductSelection from '@/pages/front-office/order/steps/NoLimitListProductSelection';
import useIndexedDB from '@/hooks/useIndexedDB';
import { PaymentConfirmedProvider } from '@/contexts/PaymentConfirmedContext';
import ProtectedPaymentRoutes from '@/components/front-office/orders/ProtectedPaymentRoutes';
import useWebSocket from '@/hooks/useWebSocket';
import MentionsLegales from '@/pages/MentionsLegales';
import CGV from '@/pages/CGV';
import Footer from '@/components/common/Footer';
import DefaultRestaurantPage from '@/pages/back-office/settings/DefaultRestaurantPage';
import BOStocks from '@/pages/back-office/stocks/BOStocks';
import RestaurantsList from '@/pages/back-office/settings/restaurants/RestaurantsList';
import BOLayout from './pages/back-office/BOLayout';
import BOBarcodes from './pages/back-office/BOBarcodes';
import BAAuditLogs from '@/pages/back-office/BAAuditLogs';

const App = () => {
	const { isInitialized, initDB } = useIndexedDB();

	useEffect(() => {
		if (!isInitialized) {
			initDB();
		}
	}, []);

	useWebSocket('WS_CONNECTION', () => { });

	return (
		<div className='h-[100dvh] lg:h-[calc(100dvh-36px)]'>
			<BrowserRouter>
				<main className="App flex flex-col bg-[url('/src/assets/background.jpg')] bg-cover h-full">
					<UserProvider>
						<SettingsProvider>
							<ToastContainer />
							<div className="hidden md:block">
								<RestaurantsProvider>

								</RestaurantsProvider>
							</div>

							<PopupNotification />

							<div className="flex-grow">
								<Suspense fallback={<p>Chargement ...</p>}>
									<Routes>
										{/* Front Office */}
										<Route path={routes.home}>
											<Route path="" element={<Home />} />
											<Route path="login" element={<LoginPage />} />
											<Route path="signup" element={<SignupPage />} />
											<Route path="reset-password" element={<ResetPasswordPage />} />
											<Route path="mentions-legales" element={<MentionsLegales />} />
											<Route path="cgv" element={<CGV />} />

											{/* Orders */}
											<Route path="commande/">
												<Route
													element={
														<OrdersProvider>
															<ProtectedOrdersRoutes />
														</OrdersProvider>
													}
												>
													<Route path="adresse" element={<AddressAutocomplete />} />
													<Route path="menu/">
														<Route path="" element={<Menu />} />
														<Route path="categories" element={<CategorieProductSelection />} />
													</Route>

													<Route element={
														<PaymentConfirmedProvider>
															<ProtectedPaymentRoutes />
														</PaymentConfirmedProvider>
													}>
														<Route path="paiement" element={<Payment />} />
														<Route path="payment-form" element={<PaymentForm />} />
														<Route path='confirmation' element={<PaymentSuccess />} />
													</Route>


													<Route path=":categoryId" element={<NoLimitCategoryProductSelection />} />
													<Route path="liste-des-produits" element={<NoLimitListProductSelection />} />
												</Route>
											</Route>

										<Route path={'/menu-page'} element={<MenuPage />} />
									</Route>
									<Route element={<PrivateRoute />}>
										{/* Front Office */}
										<Route path={'/me'} element={<EditUserInfo />} />

											{/* Back Office */}
											<Route
												element={
													<RestaurantsProvider>
														<BOAdminPrivateRoute />
													</RestaurantsProvider>
												}
											>
												<Route path={'/back-office'} element={<BODashboard />} />
												<Route path="/back-office/dashboard" element={<BODashboard />} />
												<Route path="/back-office/statistics" element={<BOStatisticsPage />} />
												<Route path="/back-office/stocks" element={<BOStocks />} />

												{/* Orders */}
												<Route path={`/back-office/restaurants/:restaurantId/commandes`} element={<BOOrders />} />
												<Route
													path={`/back-office/restaurants/:restaurantId/commandes/:orderReference/details`}
													element={<BOOrderDetails />}
												/>


												{/* Customers */}
												<Route path={`/back-office/restaurants/:restaurantId/clients`} element={<BOCustomers />} />
												{/* Notifications */}
												<Route path="/back-office/notifications/list" element={<NotificationList />} />
												<Route path="/back-office/notifications/create" element={<NotificationForm />} />
												<Route path="/back-office/notifications/:notificationId/edit" element={<NotificationForm />} />


												{/* Delivery taxes */}
												<Route path={`/back-office/settings/delivery_zones/list`} element={<DeliveryZonesList />} />
												<Route
													path={`/back-office/settings/frais-de-livraison/creation`}
													element={<DeliveryZoneCreationForm />}
												/>
												<Route
													path={`/back-office/settings/frais-de-livraison/:deliveryFeeId/edition`}
													element={<DeliveryZoneEditionForm />}
												/>

												{/* Route pour la gestion des codes-barres */}
												<Route path={`/back-office/barcodes`} element={<BOBarcodes />} />
												<Route path={`/back-office/restaurants/:restaurantId/barcodes`} element={<BOBarcodes />} />
												<Route
													path={`/back-office/settings/restaurant-par-defaut`}
													element={<DefaultRestaurantPage />}
												/>

												{/* Restaurants */}
												<Route path={`/back-office/restaurants/:restaurantId/view`} element={<BORestaurantHome />} />
												<Route path={`/back-office/restaurants/creation`} element={<BORestaurant />} />

												{/* Route pour la gestion des codes promo */}
												<Route path="/back-office/restaurants/:restaurantId/promos" element={<PromoDashboard />} />
												<Route path="/back-office/restaurants/:restaurantId/promos/creation" element={<PromoForm />} />
												<Route
													path="/back-office/restaurants/:restaurantId/promos/:promoId/edition"
													element={<PromoForm />}
												/>

												{/* Menu */}
												<Route path={`/back-office/restaurants/:restaurantId/menu`} element={<BOMenu />} />
												<Route path={`/back-office/restaurants/:restaurantId/menu/creation`} element={<MenuCreation />} />
												<Route
													path={`/back-office/restaurants/:restaurantId/menu/:menuId/edition`}
													element={<MenuEdition />}
												/>

												{/* Products */}
												<Route path={`/back-office/restaurants/:restaurantId/products`} element={<ProductsList />} />
												<Route
													path={`/back-office/restaurants/:restaurantId/products/creation`}
													element={<ProductCreation />}
												/>
												<Route
													path={`/back-office/restaurants/:restaurantId/products/:productId/edition`}
													element={<ProductEdition />}
												/>

												{/* Settings - Categories */}
												<Route path="/back-office/settings/settingmenu" element={<SettingMenu />} />
												<Route path="/back-office/settings/categories/list" element={<CategoriesList />} />
												<Route path="/back-office/settings/categories/creation" element={<CategoryCreation />} />
												<Route path="/back-office/settings/categories/:categoryId/edition" element={<CategoryEdition />} />

												{/* Settings - Restaurants */}
												<Route path="/back-office/settings/restaurants/list" element={<RestaurantsList />} />

												{/* Settings - VATs */}
												<Route path="/back-office/settings/tva/list" element={<VatsList />} />
												<Route path="/back-office/settings/tva/creation" element={<VatCreation />} />
												<Route path="/back-office/settings/tva/:vatId/edition" element={<VatEdition />} />

												{/* Settings - App Settings */}
												<Route path="/back-office/settings/app-settings" element={<AppSettings />} />

												{/* Settings - Referral Settings */}
												<Route path="/back-office/settings/referral-settings" element={<ReferralSettings />} />

												{/* Settings - Audit Logs */}
												<Route path="/back-office/settings/audit-logs" element={<BAAuditLogs />} />

												{/* Settings - Users */}
												<Route path="/back-office/settings/users/list" element={<UsersList />} />
												<Route path="/back-office/settings/users/creation" element={<UserCreation />} />
												<Route path="/back-office/settings/users/:emailParam/edition" element={<UserEdition />} />
											</Route>
										</Route>

										{/* 404 Route */}
										<Route path="*" element={<NotFound />} />
									</Routes>
								</Suspense>
							</div>
						</SettingsProvider>
					</UserProvider>
				</main>

				<Footer />
			</BrowserRouter>
		</div>
	);
};


export default App;
